import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutGrid,
  Users,
  Swords,
  Trophy,
  Printer,
  Settings,
  MessageSquare,
  UserCog,
  FileText,
  type LucideIcon,
  LayoutList,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getDirectorMessagesUnreadCount } from '@/lib/api/messages';
import { ApiError } from '@/lib/api/core';
import { usePolledData } from '@/lib/usePolledData';

// Story 082 — tournament hub sidebar. Items 2-5 navigate to the
// existing sub-page routes; Overview is the hub itself. NavLink's
// `end` prop on Overview ensures it doesn't stay active when a
// sub-route is opened. Story 087 adds Messages with a polled
// unread badge.

interface SidebarItem {
  slug: string;
  labelKey: string;
  icon: LucideIcon;
  to: (id: string) => string;
  end?: boolean;
  /** Story 094 — owner-only items are hidden for co-directors. */
  ownerOnly?: boolean;
  /** Story 097 — chess-only items are hidden for other games. */
  chessOnly?: boolean;
  /** Story 109 — backgammon-only items (OBTR rating export). */
  backgammonOnly?: boolean;
}

const ITEMS: SidebarItem[] = [
  { slug: 'overview', labelKey: 'overview', icon: LayoutGrid, to: (id) => `/tournaments/${id}`, end: true },
  { slug: 'players', labelKey: 'players', icon: Users, to: (id) => `/tournaments/${id}/players` },
  { slug: 'rounds', labelKey: 'rounds', icon: Swords, to: (id) => `/tournaments/${id}/rounds` },
  { slug: 'standings', labelKey: 'standings', icon: Trophy, to: (id) => `/tournaments/${id}/standings` },
  { slug: 'messages', labelKey: 'messages', icon: MessageSquare, to: (id) => `/tournaments/${id}/messages` },
  { slug: 'print', labelKey: 'print', icon: Printer, to: (id) => `/tournaments/${id}/print` },
  // Story 106 — preferred table numbers (owner-only, like other write pages).
  { slug: 'preferred-tables', labelKey: 'preferred_tables', icon: LayoutList, to: (id) => `/tournaments/${id}/preferred-tables`, ownerOnly: true },
  { slug: 'fide-export', labelKey: 'fide_export', icon: FileText, to: (id) => `/tournaments/${id}/fide-export`, chessOnly: true },
  { slug: 'rating-export', labelKey: 'rating_export', icon: FileText, to: (id) => `/tournaments/${id}/rating-export`, backgammonOnly: true },
  { slug: 'co-directors', labelKey: 'co_directors', icon: UserCog, to: (id) => `/tournaments/${id}/co-directors`, ownerOnly: true },
  { slug: 'edit', labelKey: 'edit', icon: Settings, to: (id) => `/tournaments/${id}/edit` },
];

interface Props {
  tournamentId: string;
  /** Story 093 — the viewer's role; owner-only items are hidden for
   *  co-directors. */
  role: 'owner' | 'co-director';
  /** Story 097 — the tournament's game; chess-only items are hidden for
   *  other games. Undefined while the tournament is still loading. */
  game?: string;
  /** Called after a sidebar nav happens — used by the mobile drawer
   *  to close itself once the user picks an item. */
  onNavigate?: () => void;
}

function MessagesUnreadBadge({ tournamentId }: { tournamentId: string }) {
  const { data } = usePolledData<number>(
    () =>
      getDirectorMessagesUnreadCount(tournamentId)
        .then((r) => r.count)
        .catch((err: unknown) => {
          throw err instanceof ApiError ? new Error(err.userMessage) : err;
        }),
    [tournamentId],
  );
  if (!data || data <= 0) return null;
  return (
    <Badge data-testid="sidebar-messages-unread" variant="default">
      {data}
    </Badge>
  );
}

export function TournamentHubSidebar({ tournamentId, role, game, onNavigate }: Props) {
  const { t } = useTranslation('director');
  const items = ITEMS.filter(
    (item) =>
      (!item.ownerOnly || role === 'owner') &&
      (!item.chessOnly || game === 'chess') &&
      (!item.backgammonOnly || game === 'backgammon'),
  );
  return (
    <nav className="flex flex-col gap-1">
      {items.map(({ slug, labelKey, icon: Icon, to, end }) => (
        <NavLink
          key={slug}
          to={to(tournamentId)}
          end={end}
          onClick={() => onNavigate?.()}
          data-testid={`tournament-hub-sidebar-item-${slug}`}
          className={({ isActive }) =>
            'flex items-center gap-2 rounded px-2 py-1.5 text-sm ' +
            (isActive
              ? 'bg-primary text-primary-foreground'
              : 'hover:bg-accent/30')
          }
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
          <span>{t(`tournament_hub.sidebar.${labelKey}`)}</span>
          {slug === 'messages' && tournamentId && (
            <MessagesUnreadBadge tournamentId={tournamentId} />
          )}
        </NavLink>
      ))}
    </nav>
  );
}
